import { useMutation } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { updateOpening } from '@/api-clients/update-opening';
import { useToaster } from '@/components/base/toaster';
import { useJobPostingContext } from '@/contexts/job-posting-context';

interface UseUpdateOpeningDescriptionOptions {
  onSuccess?: (openingExternalId: string) => void;
}

export function useUpdateOpeningDescription(
  options?: UseUpdateOpeningDescriptionOptions,
) {
  const intl = useIntl();
  const { notifySuccess, notifyError } = useToaster();
  const { hookForm } = useJobPostingContext();
  const { getValues } = hookForm;

  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn: updateOpening,
    onSuccess: (_, { openingExternalId }) => {
      // Find the opening title from the form data
      const jobDetails = getValues('jobDetails');
      const openingTitle = jobDetails?.[openingExternalId]?.title || 'Opening';

      notifySuccess({
        message: intl.formatMessage(
          {
            defaultMessage: '{openingName} - updated successfully',
            description: 'Success message for updating the opening description',
          },
          { openingName: openingTitle },
        ),
      });

      options?.onSuccess?.(openingExternalId);
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Update failed. Please try again.',
          description: 'Error message for updating the opening description',
        }),
      });
    },
  });

  return {
    updateOpeningDescription: mutate,
    updateOpeningDescriptionAsync: mutateAsync,
    isUpdatingOpening: isPending,
  };
}
